import type { ShopifyCart, ShopifyCartItem, Cart, FrontEndCartItem } from '../types';

/**
 * Generate a unique key for a cart item if one doesn't exist
 * Uses Shopify's line item ID as the base key
 */
function generateItemKey(item: ShopifyCartItem): string {
  // If Shopify provides a key, use it
  if (item.key) {
    return item.key;
  }
  // Otherwise, generate one from the line item ID
  return `cart_item_${item.id}`;
}

/**
 * Convert Shopify cart price from cents to currency units
 */
function centsToCurrency(cents: number): number {
  return cents / 100;
}

/**
 * Extract collection_id from cart item properties or product metadata
 * This may need to be enhanced if collection info is stored elsewhere
 */
function extractCollectionId(item: ShopifyCartItem): string | undefined {
  // Check if collection_id is in properties
  if (item.properties?.collection_id) {
    return String(item.properties.collection_id);
  }
  // Additional extraction logic can be added here
  // if collection data is available from other sources
  return undefined;
}

/**
 * Transform Shopify cart.js format to TestCart format required by discountCalculator
 */
export function transformToAPICart(
  shopifyCart: ShopifyCart,
  customerId?: string,
  customerTags?: string[],
  voucherCodes?: string[],
  bogoCodes?: string[],
  quantityCodes?: string[],
  productCodes?: string[],
  liveSessionId?: string
): Cart {
  const items: FrontEndCartItem[] = shopifyCart.items.map((item) => {
    const key = generateItemKey(item);

    return {
      key,
      product_id: String(item.product_id),
      variant_id: String(item.variant_id),
      collection_id: extractCollectionId(item),
      price: centsToCurrency(item.price),
      quantity: item.quantity,
      title: item.title || `${item.variant_title || ''} ${item.title || ''}`.trim(),
      line_item_id: item.id,
      auto_added: item.properties?._auto_added == true || item.properties?._auto_added === 'true',
      properties: item.properties,
    };
  });

  return {
    items,
    customer_id: customerId,
    customer_tags: customerTags,
    voucher_codes: voucherCodes,
    bogo_codes: bogoCodes,
    quantity_codes: quantityCodes,
    product_codes: productCodes,
    ...(liveSessionId ? { live_session_id: liveSessionId } : {}),
  };
}

